import type React from 'react';
import TagAnalysisChart from '../charts/TagAnalysisChart';
import type { TagBasedAnalysis } from '../../types/reporting.types';

interface CostpointOption {
  value: string;
  label: string;
}

interface TagAnalysisSectionProps {
  availableTags: string[];
  availableCostpoints: CostpointOption[];
  selectedCostpoint: string;
  selectedTags: string[];
  filteredTagBreakdown: TagBasedAnalysis[];
  onCostpointChange: (costpoint: string) => void;
  onTagToggle: (tag: string) => void;
}

const TagAnalysisSection: React.FC<TagAnalysisSectionProps> = ({
  availableTags,
  availableCostpoints,
  selectedCostpoint,
  selectedTags,
  filteredTagBreakdown,
  onCostpointChange,
  onTagToggle,
}) => {
  return (
    <div className="mb-6 bg-white overflow-hidden shadow rounded-lg">
      <div className="p-4 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tag-Based Fine-Grained Analysis</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select costpoint and tags to analyze specific budget items performance (e.g.,
          "cs-kausimaksut")
        </p>

        {/* Costpoint Selector */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Costpoint (optional filter)
          </label>
          <select
            value={selectedCostpoint}
            onChange={(e) => onCostpointChange(e.target.value)}
            className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="all">All Costpoints</option>
            {availableCostpoints.map((costpoint) => (
              <option key={costpoint.value} value={costpoint.value}>
                {costpoint.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tag Selector */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Tags for Analysis
            {selectedCostpoint !== 'all' && (
              <span className="text-xs text-gray-500 ml-1">
                (filtered by {selectedCostpoint.toUpperCase()})
              </span>
            )}
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                onClick={() => onTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Tag Analysis Results */}
        {filteredTagBreakdown.length > 0 ? (
          <TagAnalysisChart tagAnalysis={filteredTagBreakdown} />
        ) : selectedTags.length > 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No data found for the selected tags in this period</p>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>Select tags above to see detailed analysis</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TagAnalysisSection;
